<template>
  <div>
    <div class="content">
      <loading v-if="loading" text='Loading. Please wait...'/>
      <div v-show="!loading">
        <h1>My Folders</h1>
        <span v-show="error" class="help is-danger">{{error}}</span>

        <div class="right-tools no-select">
          <div class="loading-small-text" v-if="uploadingChanges">Uploading changes <loading :small="true"/></div>
          <Popover ref="folderActionPopover" content="popover" placement="bottom">
            <div class="icon-button" ref="actionsButton"><i class="fal fa-plus"></i></div>
            <div slot="content">
              <div class="list-item" v-on:click.stop="folderAction('Add Folder')">
                <span class="popover-list-image">
                  <i class="fal fa-folder"></i>
                </span>
                <span class="action-item">Add folder</span>
              </div>
              <div class="list-item" v-on:click.stop="folderAction('Add File')">
                <span class="popover-list-image">
                  <i class="fal fa-file"></i>
                </span>
                <span class="action-item"><input type="file" :accept="uploadFileFormat" @change="uploadResource" ref="fileToUpload" style="display: none" >Add file</span>
              </div>
              <div class="list-item" v-on:click.stop="folderAction('Add Flashcard Deck')">
                <span class="popover-list-image">
                  <i class="fal fa-plus"></i>
                </span>
                <span class="action-item">Add flashcard deck</span>
              </div>
            </div>
          </Popover>
        </div>

        <br>

        <!-- Rename Modal -->
        <div v-if="currentAction === 'Rename'" @click="closePopovers()" class="modal no-select">
          <div class="modal-content" @click.stop=";">
            <h3 class="has-text-left">Rename</h3>
            <p class="has-text-left">Please enter the new name for the {{ currentActionItem.folderId ? 'folder' : 'resource'}}</p>
            <form v-on:submit.prevent="renameItem()">
              <input type="text" class="focusable" v-model="currentActionItem.newTitle" maxlength="100">
            </form>
            <div class="modal-buttons is-pulled-right">
              <button v-on:click.stop="closePopovers()" class="button">Cancel</button>
              <button :disabled="currentActionItem.newTitle === ''" v-on:click.stop="renameItem()"  class="button is-primary">Save</button>
            </div>
          </div>
        </div>

        <!-- Delete Folder Modal -->
        <div v-if="currentAction === 'Delete'" @click="closePopovers()" class="modal no-select">
          <div class="modal-content" @click.stop=";">
              <h3 class="has-text-left">Confirm deletion</h3>
              <p class="has-text-left">Are you sure you want to delete the selected {{ currentActionItem.folderId ? 'folder' : 'resource'}}?</p>
            <div class="modal-buttons is-pulled-right">
              <button class="button is-primary no-button" v-on:click.stop="closePopovers()">No</button>
              <button  v-on:click.stop="deleteItem()" class="button yes-button">Yes</button> 
            </div>
          </div>
        </div>

        <!-- Add Folder Modal -->
        <div v-if="currentAction === 'Add Folder'" @click="closePopovers()" class="modal no-select">
          <div class="modal-content" @click.stop=";">
            <p class="has-text-left">Please enter the name of the new folder</p>
            <form v-on:submit.prevent="addFolder()">
              <input type="text" class="focusable" v-model="newFolderName" maxlength="100">
            </form>
            <div class="modal-buttons is-pulled-right">
              <button v-on:click.stop="closePopovers()" class="button">Cancel</button>
              <button :disabled="newFolderName === ''" v-on:click.stop="addFolder()" class="button is-primary">Save</button>
            </div>
          </div>
        </div>

        <!-- Move Modal -->
        <div v-if="currentAction === 'Move'" @click="closePopovers()" class="modal no-select">
          <div class="modal-content" @click.stop=";">
            <FolderView 
              maxHeight="300px"
              :itemToMove="currentActionItem"
              :currentFolder="currentFolderToMoveItemTo" 
              :navFolder="navCurrentMoveFolder"
              :foldersFlatMap="foldersFlatMap"
            />
            <div class="modal-buttons is-pulled-right">
              <button v-on:click.stop="closePopovers()" class="button">Cancel</button>
              <button v-on:click.stop="moveItem()" class="button is-primary" :disabled="currentFolderToMoveItemTo.folderId === currentFolder.folderId">Move</button>
            </div>
          </div>
        </div>

        <FolderView 
          :currentFolder="currentFolder" 
          :descriptionUpdated="descriptionUpdated" 
          :navFolder="navFolder"
          :foldersFlatMap="foldersFlatMap"
          :subfolderAction="subfolderAction"
          :resourceAction="resourceAction"
        />
      </div>
    </div>
    <!-- add Flashcard Deck Modal -->
    <div class="modal" v-if="showFlashcardEditor">
      <FlashcardEditor
        v-bind="{
          flashcardDeckTitle: true, 
          modalView: true, 
          showFlashcardDeckList: false,
          currentFolder,
          postMyFolderContent,
          flashcardDeckObject,
          flashcardDeckBeforeEditing
        }"
        @returnToDeckList="showFlashcardEditor = false; showFlashcardDeckList = false;"
        @myFolderFlashcardDeckEdit="onFlashcardRenamed"
        @flashcardClose="showFlashcardEditor = false;"
        @renameFlashcardDeckInFolder="val => {currentActionItem.newTitle = val; renameItem()}"
      >
      </FlashcardEditor>
    </div>
  </div>
</template>

<script>
  const _ = require('lodash');
  const moment = require('moment');
  require('moment-timezone');
  moment.tz.setDefault('Africa/Johannesburg');
  
  import Vue from 'vue';
  import utils from '@/utils/utils.js';
  import serverUtils from '@/utils/serverUtils.js';
  import error from '@/utils/error.js';
  import storeHelpers from '@/utils/storeHelpers.js';
  import analytics from '@/utils/analytics.js';

  import FolderView from './FolderView';
  import Popover from '@/components/widgets/popover';
  import FlashcardEditor from '../flashcards/FlashcardEditor';

  export default {
    name: 'MyFolders',
    components: {
      FolderView,
      Popover,
      FlashcardEditor
    },
    data: function () {
      var t = this;

      return {
        loading: true,
        uploadingChanges: false,
        myFoldersGroup: [],
        foldersFlatMap: {},
        currentFolder: [],
        currentFolderToMoveItemTo: [],
        moment: moment,
        utils: utils,
        error: '',
        unwatchFunction: undefined,
        currentAction: '',
        currentActionItem: {},
        newFolderName: '',
        newFlashcardDeck: '',
        uploadFileFormat: '.png, .jpg, .jpeg, .doc, .docx, .ppt, .pptx, .mp3, .mp4, .pdf, .xls, .xlsx, .txt',
        showFlashcardEditor: false,
        flashcardDeckObject: {},
        flashcardDeckBeforeEditing: {}
      }
    },
    beforeRouteLeave: function (to, from, next) {
      if (this.uploadingChanges) {
        if (window.confirm('Your file is still busy uploading, are you sure you want to exit and cancel your changes?')) {
          next();
        } else {
          next(false);
        }
      } else {
        next();
      }
    },
    destroyed: function() {
      this.unwatchFunction();
    },
    beforeMount() { 
      window.addEventListener("beforeunload", event => {
        if (!this.uploadingChanges) return
        event.preventDefault()
        event.returnValue = ""
      })
    },
    mounted: function () {
      this.unwatchFunction = utils.watchStoreProperty(this, 'myFolders', this.myFoldersUpdated);
      analytics.freeform(this);
      storeHelpers.checkActive(this);

      var t = this;
      document.addEventListener("keydown", function(e) {
        if (e.keyCode === 27) {
          t.closePopovers();
        }
      });
    },
    watch:{
      $route: function (to, from) {
        if(to.params.id === 'myfolders') {
          this.currentFolderChanged(this.myFoldersGroup.data[0]);
        } else {
          this.currentFolderChanged(this.getFolder(to.params.id));
        }
        
        if (to.query.edit) {
          var resource = storeHelpers.getResourceById(this, to.query.edit);
          this.resourceAction("Edit", resource);
        }
        if (to.query.delete) {
          this.deleteItemById(to.query.delete);
        }
      }
    },
    methods: {
      myFoldersUpdated: function(folders) {
        var t = this;

        // if no my folders yet, create a my folders
        if(!folders || folders.length === 0) {
          folders = [{
            "group": t.$store.state.user.username,
            "name": "usercontent",
            "data": [
              {
                "desc": "",
                "title": "My Folders",
                "children": [],
                "resources": [],
                "folderId": "myfolders"
              }
            ],
            "category": "usercontent"
          }]
        }

        const groupToFind = _.find(folders, function(group) {
          return group.name === 'usercontent';
        });

        if(groupToFind && groupToFind.data && groupToFind.data.length > 0) {
          t.myFoldersGroup = groupToFind;

          t.foldersFlatMap = {
            'myfolders': t.myFoldersGroup.data[0]
          };
          
          t.populateParentsAndFlatmap(t.myFoldersGroup.data[0]);

          if(t.$route.params.id === 'myfolders') {
            t.currentFolderChanged(t.myFoldersGroup.data[0]);
          } else {
            var folder = t.getFolder(t.$route.params.id);
            t.currentFolderChanged(folder);
          }
        }

        this.loading = false;
      },
      navFolder: function(folder) {
        this.$router.push('/folders/' + folder.folderId);
      },
      navCurrentMoveFolder: function(folder) {
        this.currentFolderToMoveItemTo = folder;
        this.populateBreadcrumb(folder);
      },
      populateParentsAndFlatmap: function (folder) {
        var t = this;

        t.foldersFlatMap[folder.folderId] = folder;

        if(!folder.children) return;

        _.each(folder.children, function (child){
          child.parentFolderId = folder.folderId;
          t.populateParentsAndFlatmap(child);
        });
      },
      currentFolderChanged: function(folder) {
        this.currentFolder = folder;
        this.populateBreadcrumb(folder);
      },
      populateBreadcrumb(folder) {
        // populate breadcrumb
        if(!folder.breadcrumbs) {
          var breadcrumbArray = [folder.folderId];

          var parentFolderId = folder.parentFolderId;
          while(parentFolderId) {
            breadcrumbArray.push(parentFolderId);
            parentFolderId = this.foldersFlatMap[parentFolderId].parentFolderId;
          }

          Vue.set(folder, 'breadcrumbs', breadcrumbArray.reverse().slice(1));
        }
      },
      getFolder: function(folderId) {
        var folderFound;
        this.myFoldersGroup.data[0].children.some(function searchFolder (folder) {
          if (folder.folderId === folderId) {
            folderFound = folder;
            return true;
          }
          return Array.isArray(folder.children) && folder.children.some(searchFolder);
        });
        return folderFound;
      },
      subfolderAction: function(action, folder) {
        var t = this;
        Vue.set(folder, 'newTitle', folder.title);
        t.currentFolderToMoveItemTo = t.currentFolder;

        t.currentAction = action;
        t.currentActionItem = folder;

        Vue.nextTick(function() {
          var focusables = document.getElementsByClassName('focusable')
          if(focusables.length > 0) {
            focusables[0].focus();
            focusables[0].select();
          }
        });
      },
      folderAction: function(action) {
        var t = this;

        this.$refs.folderActionPopover.hidePopper();

        t.currentAction = action;
        this.newFolderName = '';

        Vue.nextTick(function() {
          var focusables = document.getElementsByClassName('focusable')
          if(focusables.length > 0) {
            focusables[0].focus();
            focusables[0].select();
          }
        });
        
        if (action === "Add File") {
          t.$refs.fileToUpload.click();
        } else if(action === "Add Flashcard Deck") {
          t.addFlashcardDeck();
        }
      },
      resourceAction: function(action, resource) {
        var t = this;
        if (action === "Download") {
          var absoluteURL = this.$config.get('ecApi') + 'resources/' + resource.id + '/data?auth_token=' + this.$store.state.user.cookieToken;
          utils.downloadFile(absoluteURL, resource.title);
        } else {
          if (action === "Edit") {
            var deckData = {};
            utils.log(this, "resource:", resource);
            utils.openFlashcardDeckById(this, resource._id ? resource._id : resource.id, function(res){
              deckData.currentFlashcardDeckId = resource._id ? resource._id : resource.id;
              deckData.flashcardDeckObject = res.data;
              deckData.currentFlashcardDeckTitle = resource.title;
              deckData.flashcardDeckEditor = true;

              t.flashcardDeckObject = res.data;
              t.flashcardDeckBeforeEditing = deckData;

              t.showFlashcardEditor = true;
            });
          }
          Vue.set(resource, 'newTitle', resource.title);
          t.currentFolderToMoveItemTo = t.currentFolder;

          t.currentAction = action;
          t.currentActionItem = resource;

          Vue.nextTick(function() {
            var focusables = document.getElementsByClassName('focusable')
            if(focusables.length > 0) {
              focusables[0].focus();
              focusables[0].select();
            }
          });
        }
      },
      closePopovers: function() {
        this.currentActionItem = {};
        this.currentAction = '';
        this.newFlashcardDeck = '';
      },
      descriptionUpdated: function() {
        this.postMyFolderContent();
        analytics.changeFolderDescription(this, this.currentFolder.folderId, this.myFoldersGroup.group, 1);
      },
      addFolder: function() {
        var newFolder = {
          desc: '',
          title: this.newFolderName,
          children: [],
          folderId: utils.GUID(),
          resources: [],
          parentFolderId: this.currentFolder.folderId
        };

        this.currentFolder.children.push(newFolder);
        Vue.set(this.currentFolder, 'children', this.currentFolder.children);
        this.foldersFlatMap[newFolder.folderId] = newFolder;

        this.postMyFolderContent();
        this.closePopovers();

        // addFolder: function(handle, folder, groupId, location = 0)
        analytics.addFolder(this, newFolder, this.myFoldersGroup.group, 0);
      },
      renameItem: function() {
        // only do stuff if name is different
        if(this.currentActionItem.title !== this.currentActionItem.newTitle) {
          var oldName = this.currentActionItem.title;
          Vue.set(this.currentActionItem, 'title', this.currentActionItem.newTitle);
          this.postMyFolderContent();
          
          if (this.currentActionItem.folderId) {
            //renameFile: function(handle, oldFileName, newFileName, groupId, location = 0)
            analytics.renameFile(this, oldName, this.currentActionItem.newTitle, this.myFoldersGroup.group, 0);
          } else {
            // renameFolder: function(handle, folderId, groupId, oldFolderName, newFolderName, location = 0)
            analytics.renameFolder(this, this.currentActionItem.folderId, this.myFoldersGroup.group, oldName, this.currentActionItem.newTitle, 0);
          }
        }

        this.closePopovers();
      },
      moveItem: function() {
        var t = this;
        // FOLDER
        if(t.currentActionItem.folderId) {
          // if in a new location
          if(this.currentActionItem.parentFolderId !== this.currentFolderToMoveItemTo.folderId) {
            // remove from current parent
            if(t.currentActionItem.parentFolderId) {
              var parentFolder = t.foldersFlatMap[t.currentActionItem.parentFolderId];
              if(parentFolder) {
                parentFolder.children = _.filter(parentFolder.children, function(child) {
                  return child.folderId !== t.currentActionItem.folderId;
                });
              }
            }

            // add to new parent
            if(!t.currentFolderToMoveItemTo.children) {
              Vue.set(t.currentFolderToMoveItemTo, 'children', []);
            }
            t.currentFolderToMoveItemTo.children.push(t.currentActionItem);
            var oldParentFolderId = t.currentActionItem.parentFolderId;
            t.currentActionItem.parentFolderId = t.currentFolderToMoveItemTo.folderId;

            //moveFolder: function(handle, folderId, groupId, oldParentFolderId, newParentFolderId, location = 0)
            analytics.moveFolder(t, t.currentActionItem.folderId, this.myFoldersGroup.group, oldParentFolderId, t.currentActionItem.parentFolderId, 0);
            t.postMyFolderContent();
          }
        } else { // FILE
          // if in a new location
          if(this.currentFolder.folderId !== this.currentFolderToMoveItemTo.folderId) {
            // remove from current folder
            this.currentFolder.resources = _.filter(this.currentFolder.resources, function(resource) {
              return resource.id !== t.currentActionItem.id;
            });

            if(!t.currentFolderToMoveItemTo.resources) {
              Vue.set(t.currentFolderToMoveItemTo, 'resources', []);
            }
            // add to new folder
            t.currentFolderToMoveItemTo.resources.push(t.currentActionItem);
            // moveFile: function(handle, fileId, groupId, oldParentFolderId, newParentFolderId, location = 0)
            analytics.moveFile(this, this.currentActionItem.id, this.myFoldersGroup.group, this.currentFolder.folderId, this.currentFolderToMoveItemTo.folderId, 0);
            this.postMyFolderContent();
          }
        }

        // todo stats

        this.closePopovers();
      },
      deleteItem: function() {
        var t = this;
        // FOLDER
        if(t.currentActionItem.folderId) {
          if(t.currentActionItem.parentFolderId) {
            var parentFolder = t.foldersFlatMap[t.currentActionItem.parentFolderId];
            if(parentFolder) {
              parentFolder.children = _.filter(parentFolder.children, function(child) {
                return child.folderId !== t.currentActionItem.folderId;
              });
              //  deleteFolder: function(handle, folderId, groupId, location = 0)
              analytics.deleteFolder(t, t.currentActionItem.folderId, t.myFoldersGroup.group, 0);
              delete t.foldersFlatMap[t.currentActionItem.folderId];
            }
          }
        } else { // FILE
          storeHelpers.removeResource(t, t.currentActionItem.id);
          t.currentFolder.resources = _.filter(t.currentFolder.resources, function(resource) {
            return resource.id !== t.currentActionItem.id;
          });
          // deleteFile: function(handle, fileId, groupId, location = 0)
          analytics.deleteFile(t, t.currentActionItem.id, t.myFoldersGroup.group, 0);
        }

        t.postMyFolderContent();
        t.closePopovers();
      },
      deleteItemById: function(resourceID) {
        storeHelpers.removeResource(this, resourceID);
        this.currentFolder.resources = _.filter(this.currentFolder.resources, function(resource) {
          return resource.id !== resourceID;
        });
        analytics.deleteFile(this, resourceID, this.myFoldersGroup.group, 0);
        this.postMyFolderContent();
        this.closePopovers();
      },
      uploadResource: async function() {
        var t = this;
        var filename = t.$refs.fileToUpload.files[0].name;
        
        if (filename.length > 100) {
          t.error = "File name exceeds 100 characters."
          return;
        }
        var newResource = serverUtils.createResource(filename);
        t.uploadingChanges = true;
        // prepare to POST to server
        serverUtils.uploadResource(t, newResource, t.$refs.fileToUpload.files[0], function(res, e) {
          if (res === "200") {
            // add new resource to UI
            t.currentFolder.resources.push(newResource);
            storeHelpers.addResource(t, newResource);
            Vue.set(t.currentFolder, 'resources', t.currentFolder.resources);
            // on file successfully added
            // addFile: function(handle, folder, resource, groupId, location = 0)
            analytics.addFile(t, t.currentFolder, newResource, t.myFoldersGroup.group, 0);
            t.postMyFolderContent();
          } else {
            console.error("Fail uploading resource");
          }
        });        
      },
      addFlashcardDeck: function() {
        this.flashcardDeckBeforeEditing = undefined;
        this.flashcardDeckObject = undefined;
        this.showFlashcardEditor = !this.showFlashcardEditor;
      },
      deleteFile: function() {
        Vue.set(this.popoverOptions, 'deleteFolderModalActive', false);
        // deleteFile: function(handle, fileId, groupId, location = 0)
        // analytics.deleteFile(this, fileId, this.myFoldersGroup.group, location);
      },
      cleanData: function (folder) {
        var t = this;

        delete folder.newTitle;
        delete folder.breadcrumbs;

        if(folder.resources) {
          _.each(folder.resources, function (resource){
            delete resource.uploading;
          });
        }

        if(!folder.children) return;

        _.each(folder.children, function (child){
          delete child.parentFolderId;
          t.cleanData(child);
        });
      },
      postMyFolderContent: function() {
        var t = this;
        t.uploadingChanges = true;
        var myFoldersDataCleaned = JSON.parse(JSON.stringify(t.myFoldersGroup));
        t.cleanData(myFoldersDataCleaned.data[0]);
        serverUtils.postMyFolderContent(t, myFoldersDataCleaned, function(res) {
          if (res === 200) {
            t.uploadingChanges = false;
          } else {
            console.error(res);
            t.uploadingChanges = false;
            t.error = error.logAndGetError(t, e);
          }
        });
      },
      onFlashcardRenamed(resource) {
        Vue.set(this.currentActionItem, 'title', resource.title);
        this.postMyFolderContent();
        this.showFlashcardEditor = false;
      }
    }
  }

</script>

<style scoped lang="scss">

</style>